import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Users, KeyRound, FileCheck2, BookOpen, TrendingUp, Trash2, Plus, Copy, Check, X, Pencil, BarChart3, School, Award, FileText, UserPlus, IdCard } from "lucide-react";
import Reports from "@/pages/Reports";
import ClassroomsAdmin from "@/pages/ClassroomsAdmin";
import CertificatesAdmin from "@/pages/CertificatesAdmin";
import QuotationsPage from "@/pages/QuotationsPage";
import ManualCertificateAdmin from "@/pages/ManualCertificateAdmin";
import InstructorCredentialsAdmin from "@/pages/InstructorCredentialsAdmin";

export default function AdminPanel() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [codes, setCodes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [pendingQuotations, setPendingQuotations] = useState(0);

  // forms
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "student" });
  const [newCourse, setNewCourse] = useState({ title: "", description: "", price: 0, duration_hours: 10, teacher_id: "", thumbnail: "", passing_score: 80, category: "" });
  const [genCode, setGenCode] = useState({ course_id: "", quantity: 5, expires_at: "" });
  const [reject, setReject] = useState({ id: null, reason: "" });
  const [copied, setCopied] = useState(null);
  // Edit-name modal: forces admin to type a rotating 3-digit code before saving
  const [editName, setEditName] = useState(null); // { user, newName, code, typed, error }

  const loadAll = async () => {
    const [s, u, c, k, d, pq] = await Promise.all([
      api.get("/stats"),
      api.get("/users"),
      api.get("/courses"),
      api.get("/codes"),
      api.get("/documents"),
      api.get("/quotations/pending-count").catch(() => ({ data: { count: 0 } })),
    ]);
    setStats(s.data); setUsers(u.data); setCourses(c.data); setCodes(k.data); setDocuments(d.data);
    setPendingQuotations(pq.data.count || 0);
  };

  useEffect(() => { loadAll(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    try { await api.post("/users", newUser); setNewUser({ name: "", email: "", password: "", role: "student" }); loadAll(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail)); }
  };

  const delUser = async (id) => {
    if (!confirm("¿Eliminar usuario?")) return;
    await api.delete(`/users/${id}`); loadAll();
  };

  const updateUser = async (id, patch) => {
    await api.put(`/users/${id}`, patch); loadAll();
  };

  const genConfirmCode = () => String(Math.floor(100 + Math.random() * 900));

  const openEditName = (u) => {
    setEditName({ user: u, newName: u.name || "", code: genConfirmCode(), typed: "", error: "" });
  };

  const regenEditCode = () => {
    setEditName((s) => s ? { ...s, code: genConfirmCode(), typed: "", error: "" } : s);
  };

  const saveEditName = async () => {
    if (!editName) return;
    const trimmed = editName.newName.trim();
    if (!trimmed) { setEditName({ ...editName, error: "El nombre no puede quedar vacío." }); return; }
    if (editName.typed !== editName.code) {
      // Wrong code: rotate a new one so the admin can't retry by brute force
      setEditName({ ...editName, code: genConfirmCode(), typed: "", error: "Código incorrecto. Se generó un nuevo código, intenta de nuevo." });
      return;
    }
    try {
      await api.put(`/users/${editName.user.id}`, { name: trimmed });
      setEditName(null);
      loadAll();
    } catch (e) {
      setEditName({ ...editName, error: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const createCourse = async (e) => {
    e.preventDefault();
    try { await api.post("/courses", { ...newCourse, teacher_id: newCourse.teacher_id || null }); setNewCourse({ title: "", description: "", price: 0, duration_hours: 10, teacher_id: "", thumbnail: "", passing_score: 80, category: "" }); loadAll(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail)); }
  };

  const delCourse = async (id) => {
    if (!confirm("¿Eliminar curso?")) return;
    await api.delete(`/courses/${id}`); loadAll();
  };

  const assignTeacher = async (courseId, teacherId) => {
    const c = courses.find((x) => x.id === courseId);
    await api.put(`/courses/${courseId}`, { ...c, teacher_id: teacherId || null }); loadAll();
  };

  const generate = async (e) => {
    e.preventDefault();
    try { await api.post("/codes/generate", { course_id: genCode.course_id, quantity: parseInt(genCode.quantity), expires_at: genCode.expires_at || null }); loadAll(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail)); }
  };

  const reviewDoc = async (id, status, reason) => {
    await api.post(`/documents/${id}/review`, { status, rejection_reason: reason || null });
    setReject({ id: null, reason: "" });
    loadAll();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  const teachers = users.filter((u) => u.role === "instructor");

  const TabBtn = ({ id, label, icon: Icon, count }) => (
    <button data-testid={`admin-tab-${id}`} onClick={() => setTab(id)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${tab === id ? "bg-marino text-white" : "text-marino hover:bg-marino/5"}`}>
      <Icon className="w-4 h-4" /> {label} {count !== undefined && <span className="text-xs opacity-70">({count})</span>}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading font-black text-3xl sm:text-4xl text-marino tracking-tight">Panel de Administración</h1>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-gris/30 pb-3">
        <TabBtn id="stats" label="Resumen" icon={TrendingUp} />
        <TabBtn id="reports" label="Reportes" icon={BarChart3} />
        <TabBtn id="quotations" label="Cotizaciones" icon={FileText} count={pendingQuotations} />
        <TabBtn id="classrooms" label="Salón" icon={School} />
        <TabBtn id="certificates" label="Certificados" icon={Award} />
        <TabBtn id="manual-cert" label="Registro manual" icon={UserPlus} />
        <TabBtn id="credentials" label="Credenciales" icon={IdCard} />
        <TabBtn id="users" label="Usuarios" icon={Users} count={users.length} />
        <TabBtn id="courses" label="Cursos" icon={BookOpen} count={courses.length} />
        <TabBtn id="codes" label="Códigos" icon={KeyRound} count={codes.length} />
        <TabBtn id="documents" label="Documentos" icon={FileCheck2} count={documents.filter((d) => d.status === "pending").length} />
      </div>

      {/* STATS */}
      {tab === "stats" && stats && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { k: "Usuarios", v: stats.total_users, icon: Users },
            { k: "Cursos", v: stats.total_courses, icon: BookOpen },
            { k: "Inscripciones", v: stats.total_enrollments, icon: TrendingUp },
            { k: "Completados", v: stats.completed_enrollments, icon: FileCheck2 },
            { k: "Códigos usados", v: stats.codes_used, icon: KeyRound },
          ].map((s) => (
            <div key={s.k} className="bg-white border border-gris/30 rounded-xl p-5 shadow-sm">
              <s.icon className="w-5 h-5 text-naranja mb-3" />
              <div className="font-heading font-black text-3xl text-marino">{s.v}</div>
              <div className="text-sm text-marino/70">{s.k}</div>
            </div>
          ))}
        </div>
      )}

      {/* REPORTS */}
      {tab === "reports" && <div className="mt-6"><Reports /></div>}

      {/* QUOTATIONS */}
      {tab === "quotations" && <div className="mt-6"><QuotationsPage /></div>}

      {/* CLASSROOMS */}
      {tab === "classrooms" && <div className="mt-6"><ClassroomsAdmin /></div>}

      {/* CERTIFICATES */}
      {tab === "certificates" && <div className="mt-6"><CertificatesAdmin /></div>}
      {tab === "manual-cert" && <div className="mt-6"><ManualCertificateAdmin /></div>}
      {tab === "credentials" && <div className="mt-6"><InstructorCredentialsAdmin /></div>}

      {/* USERS */}
      {tab === "users" && (
        <div className="mt-6 space-y-6">
          <form onSubmit={createUser} className="bg-white border border-gris/30 rounded-xl p-5 grid sm:grid-cols-5 gap-3">
            <input data-testid="new-user-name" required placeholder="Nombre" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
            <input data-testid="new-user-email" required type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
            <input data-testid="new-user-password" required type="password" placeholder="Contraseña" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
            <select data-testid="new-user-role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm">
              <option value="student">Estudiante</option>
              <option value="instructor">Instructor</option>
              <option value="admin">Admin</option>
            </select>
            <button data-testid="create-user-btn" className="bg-naranja text-white rounded-lg text-sm font-medium hover:bg-naranjaHover"><Plus className="w-4 h-4 inline" /> Crear</button>
          </form>

          <div className="bg-white border border-gris/30 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-marino text-white">
                <tr><th className="p-3 text-left">Nombre</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Rol</th><th className="p-3 text-left">Estado</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-gris/20">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-marino">{u.name}</span>
                        <button
                          data-testid={`edit-name-${u.id}`}
                          onClick={() => openEditName(u)}
                          title="Editar nombre"
                          className="p-1 text-marino/50 hover:text-naranja hover:bg-naranja/10 rounded transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="p-3 text-marino/80">{u.email}</td>
                    <td className="p-3">
                      <select value={u.role} onChange={(e) => updateUser(u.id, { role: e.target.value })} className="text-xs border border-gris rounded px-2 py-1">
                        <option value="student">student</option><option value="instructor">instructor</option><option value="admin">admin</option>
                      </select>
                    </td>
                    <td className="p-3">
                      <select value={u.status || "active"} onChange={(e) => updateUser(u.id, { status: e.target.value })} className="text-xs border border-gris rounded px-2 py-1">
                        <option value="active">active</option><option value="disabled">disabled</option>
                      </select>
                    </td>
                    <td className="p-3 text-right"><button data-testid={`del-user-${u.id}`} onClick={() => delUser(u.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* COURSES */}
      {tab === "courses" && (
        <div className="mt-6 space-y-6">
          <form onSubmit={createCourse} className="bg-white border border-gris/30 rounded-xl p-5 grid sm:grid-cols-2 gap-3">
            <input required placeholder="Título" value={newCourse.title} onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
            <input placeholder="URL de imagen (thumbnail)" value={newCourse.thumbnail} onChange={(e) => setNewCourse({ ...newCourse, thumbnail: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
            <textarea required placeholder="Descripción" value={newCourse.description} onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })} className="sm:col-span-2 px-3 py-2 border border-gris rounded-lg text-sm" rows={2} />
            <input type="number" placeholder="Precio" value={newCourse.price} onChange={(e) => setNewCourse({ ...newCourse, price: parseFloat(e.target.value) })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
            <input type="number" placeholder="Duración (h)" value={newCourse.duration_hours} onChange={(e) => setNewCourse({ ...newCourse, duration_hours: parseInt(e.target.value) })} className="px-3 py-2 border border-gris rounded-lg text-sm" />
            <input placeholder="Categoría (ej: Seguridad y salud ocupacional)" value={newCourse.category} onChange={(e) => setNewCourse({ ...newCourse, category: e.target.value })} className="sm:col-span-2 px-3 py-2 border border-gris rounded-lg text-sm" />
            <select value={newCourse.teacher_id} onChange={(e) => setNewCourse({ ...newCourse, teacher_id: e.target.value })} className="px-3 py-2 border border-gris rounded-lg text-sm">
              <option value="">Sin instructor asignado</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button data-testid="create-course-btn" className="bg-naranja text-white rounded-lg text-sm font-medium hover:bg-naranjaHover">Crear curso</button>
          </form>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => (
              <div key={c.id} className="bg-white border border-gris/30 rounded-xl p-4">
                <h3 className="font-heading font-bold text-marino">{c.title}</h3>
                <p className="text-xs text-marino/60 mt-1 line-clamp-2">{c.description}</p>
                <div className="mt-3 text-sm">
                  <label className="text-xs text-marino/70">Instructor:</label>
                  <select value={c.teacher_id || ""} onChange={(e) => assignTeacher(c.id, e.target.value)} className="w-full mt-1 px-2 py-1 border border-gris rounded text-xs">
                    <option value="">Sin asignar</option>
                    {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="mt-3 flex justify-between items-center">
                  <span className="text-marino/70 text-xs">${c.price} · {c.duration_hours}h</span>
                  <div className="flex gap-2">
                    <Link to={`/instructor/courses/${c.id}`} data-testid={`admin-edit-course-${c.id}`} title="Editar contenido" className="text-marino hover:text-naranja"><Pencil className="w-4 h-4" /></Link>
                    <button onClick={() => delCourse(c.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CODES */}
      {tab === "codes" && (
        <div className="mt-6 space-y-6">
          <form onSubmit={generate} className="bg-white border border-gris/30 rounded-xl p-5 grid sm:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs text-marino/70">Curso</label>
              <select data-testid="gen-code-course" required value={genCode.course_id} onChange={(e) => setGenCode({ ...genCode, course_id: e.target.value })} className="w-full px-3 py-2 border border-gris rounded-lg text-sm">
                <option value="">Selecciona curso</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-marino/70">Cantidad</label>
              <input data-testid="gen-code-qty" type="number" min="1" max="100" value={genCode.quantity} onChange={(e) => setGenCode({ ...genCode, quantity: e.target.value })} className="w-full px-3 py-2 border border-gris rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-marino/70">Expira (opcional)</label>
              <input type="date" value={genCode.expires_at} onChange={(e) => setGenCode({ ...genCode, expires_at: e.target.value })} className="w-full px-3 py-2 border border-gris rounded-lg text-sm" />
            </div>
            <button data-testid="gen-code-btn" className="bg-naranja text-white rounded-lg text-sm font-medium hover:bg-naranjaHover py-2">Generar</button>
          </form>

          <div className="bg-white border border-gris/30 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-marino text-white"><tr><th className="p-3 text-left">Código</th><th className="p-3 text-left">Curso</th><th className="p-3 text-left">Estado</th><th className="p-3 text-left">Usado por</th><th className="p-3"></th></tr></thead>
              <tbody>
                {codes.map((k) => {
                  const c = courses.find((x) => x.id === k.course_id);
                  const usedBy = users.find((u) => u.id === k.used_by_user_id);
                  return (
                    <tr key={k.id} className="border-b border-gris/20">
                      <td className="p-3 font-mono text-marino">{k.code_string}</td>
                      <td className="p-3 text-marino/80">{c?.title || "-"}</td>
                      <td className="p-3">{k.is_used ? <span className="text-red-600">Usado</span> : <span className="text-green-700">Disponible</span>}</td>
                      <td className="p-3 text-marino/80">{usedBy?.name || "-"}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => copyCode(k.code_string)} className="text-marino hover:text-naranja">
                          {copied === k.code_string ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DOCUMENTS */}
      {tab === "documents" && (
        <div className="mt-6 space-y-3">
          {documents.length === 0 && <p className="text-marino/60">Sin documentos subidos.</p>}
          {documents.map((d) => (
            <div key={d.id} data-testid={`admin-doc-${d.id}`} className="bg-white border border-gris/30 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-marino">{d.document_name} <span className="text-marino/50 text-xs">({d.original_filename})</span></div>
                  <div className="text-xs text-marino/60">De: {d.user_name} · {new Date(d.created_at).toLocaleString()}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${d.status === "approved" ? "bg-green-100 text-green-800" : d.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-800"}`}>{d.status}</span>
                {d.status === "pending" && (
                  <div className="flex gap-2">
                    <button data-testid={`doc-approve-${d.id}`} onClick={() => reviewDoc(d.id, "approved")} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700"><Check className="w-4 h-4" /></button>
                    <button data-testid={`doc-reject-${d.id}`} onClick={() => setReject({ id: d.id, reason: "" })} className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-red-700"><X className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
              {d.rejection_reason && <div className="mt-2 text-sm text-red-600">Motivo rechazo: {d.rejection_reason}</div>}
              {reject.id === d.id && (
                <div className="mt-3 flex gap-2">
                  <input data-testid={`reject-reason-${d.id}`} value={reject.reason} onChange={(e) => setReject({ ...reject, reason: e.target.value })} placeholder="Motivo del rechazo" className="flex-1 px-3 py-2 border border-gris rounded-lg text-sm" />
                  <button data-testid={`reject-confirm-${d.id}`} onClick={() => reviewDoc(d.id, "rejected", reject.reason)} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm">Rechazar</button>
                  <button onClick={() => setReject({ id: null, reason: "" })} className="bg-gris text-marino px-4 py-2 rounded-lg text-sm">Cancelar</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Name — with rotating 3-digit confirmation code */}
      {editName && (
        <div data-testid="edit-name-modal" className="fixed inset-0 z-[90] bg-marino/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
            <div className="bg-marino text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 font-heading font-bold">
                <Pencil className="w-5 h-5 text-naranja" /> Editar nombre del usuario
              </div>
              <button data-testid="edit-name-close" onClick={() => setEditName(null)} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-3 bg-naranja/5 border-l-4 border-naranja rounded-lg p-3">
                <span className="w-5 h-5 text-naranja flex-shrink-0 mt-0.5">⚠️</span>
                <p className="text-marino text-sm leading-relaxed">
                  Estás por <b>corregir el nombre</b> del usuario <b>{editName.user.email}</b>. Revisa
                  cuidadosamente que el nombre escrito sea el correcto antes de guardar — este cambio
                  quedará registrado en todos los certificados y correos futuros.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-marino/70 mb-1">Nombre corregido</label>
                <input
                  data-testid="edit-name-input"
                  value={editName.newName}
                  onChange={(e) => setEditName({ ...editName, newName: e.target.value, error: "" })}
                  className="w-full border border-gris rounded-lg px-3 py-2 text-sm focus:border-marino focus:outline-none"
                />
                <div className="text-[11px] text-marino/50 mt-1">Anterior: <span className="line-through">{editName.user.name}</span></div>
              </div>

              <div className="bg-marino/5 border border-marino/10 rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wider text-marino/60 font-semibold mb-1">
                  Código de confirmación
                </div>
                <div className="flex items-center gap-3">
                  <div data-testid="edit-name-code" className="font-mono font-bold text-2xl text-naranja tracking-widest bg-white border border-gris/40 rounded px-3 py-1 select-none">
                    {editName.code}
                  </div>
                  <button
                    type="button"
                    data-testid="edit-name-regen"
                    onClick={regenEditCode}
                    title="Generar un nuevo código"
                    className="text-xs text-marino/70 hover:text-naranja underline"
                  >
                    generar otro
                  </button>
                </div>
                <p className="text-[11px] text-marino/60 mt-2">
                  Escribe el código exactamente como aparece para confirmar la corrección.
                </p>
                <input
                  data-testid="edit-name-typed"
                  value={editName.typed}
                  onChange={(e) => setEditName({ ...editName, typed: e.target.value.replace(/\D/g, "").slice(0, 3), error: "" })}
                  inputMode="numeric"
                  maxLength={3}
                  placeholder="—"
                  className="mt-2 w-32 text-center tracking-widest font-mono text-lg border border-gris rounded-lg px-3 py-2 focus:border-marino focus:outline-none"
                />
              </div>

              {editName.error && (
                <div data-testid="edit-name-error" className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {editName.error}
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  data-testid="edit-name-cancel"
                  onClick={() => setEditName(null)}
                  className="flex-1 bg-gris text-marino px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-grisHover"
                >
                  Cancelar
                </button>
                <button
                  data-testid="edit-name-save"
                  onClick={saveEditName}
                  className="flex-1 bg-naranja text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-naranjaHover inline-flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" /> Guardar corrección
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
